import React, {Fragment, Component} from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {CardElement, ElementsConsumer} from '@stripe/react-stripe-js';
import {FormControlLabel,Checkbox as CheckboxEl } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Theme, useTheme } from '@mui/material/styles';
import Button from '../../components/Button/Button';
import { PaymentCardElementControl, PaymentControl, PaymentForm, PaymentGroup, PaymentLabel } from '../../styles/jss/components/review';

interface IStripeProperties {
    componentProps: any;
    amountToPay: any;
    handleShowErrorPage: any;
    handlePaymentProcessing: any;
    showAgreement: boolean;
}

interface IStripeInnerProperties extends IStripeProperties {
    stripe: any;
    elements: any;
    cardStyle: any;
}

class StripeState {
    disableBtn: boolean = true;
    cardholderName: string = "";
    cardholderNameErrorMessage: string = "";
    cardDetailsErrorMessage: string = "";
    cardDetailsEntered: boolean = false;
    userAgreementChecked: boolean = false;
}

export default function Stripe(props: IStripeProperties) {
    const theme = useTheme();

    const getCardStyle = (theme: Theme) => {
        const cardStyle = {
            style: {
                base: {
                    color: theme.palette.text.primary,
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    fontSmoothing: "antialiased",
                    fontSize: '16px',
                    fontWeight: '400',
                    lineHeight: '1.3',
                    '::placeholder': {
                        color: theme.palette.text.disabled
                    }
                },
                invalid: {
                    color: '#ef4439',
                    iconColor: '#ef4439'
                }
            },
            hidePostalCode: true
        };

        return cardStyle;
    };

    return (
        <Elements stripe={props.componentProps.app.payment.config.stripePromise}>
            <ElementsConsumer>
                {({stripe, elements}) => (
                    <StripeInner
                        componentProps={props.componentProps}
                        amountToPay={props.amountToPay}
                        handleShowErrorPage={props.handleShowErrorPage}
                        handlePaymentProcessing={props.handlePaymentProcessing}
                        showAgreement={props.showAgreement}
                        stripe={stripe}
                        elements={elements}
                        cardStyle = {getCardStyle(theme)}
                    />
                )}
            </ElementsConsumer>
        </Elements>
    );
}

class StripeInner extends Component<IStripeInnerProperties, StripeState> {

    constructor(props: any) {
        super(props);
        this.state = new StripeState();
    }

    render () {
        return (
            <Fragment>
                <PaymentForm onSubmit={this.handleSubmit}>
                    <PaymentGroup>
                        <PaymentLabel required={true}>Card holder name</PaymentLabel>
                        <PaymentControl type="text" minLength={3} maxLength={91} value={this.state.cardholderName} onChange={this.handleCardholderChange} placeholder="Card holder name" />
                        <span className="error-message">{this.state.cardholderNameErrorMessage}</span>
                    </PaymentGroup>

                    <PaymentGroup>
                        <PaymentLabel required={true}>Card number</PaymentLabel>
                        <PaymentCardElementControl options={this.props.cardStyle} onChange={this.handleCardChange} />
                        <span className="error-message">{this.state.cardDetailsErrorMessage}</span>
                    </PaymentGroup>

                    {this.props.showAgreement &&
                        <FormControlLabel
                            label="I agree to User Agreement and Privacy Policy"
                            control={
                                <CheckboxEl
                                    size="small"
                                    color="secondary"
                                    checked={this.state.userAgreementChecked}
                                    onChange={this.handleAgreementChange}
                                    icon={<CheckBoxOutlineBlankIcon color="disabled" fontSize="small" />}
                                />
                            }
                        />
                    }

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        color="secondary"
                        fullWidth={true}
                        disableElevation
                        disabled={this.state.disableBtn}
                    >
                        {this.props.componentProps.app.payment.config.buttonText}
                    </Button>
                </PaymentForm>
            </Fragment>
        );
    }

    handleCardChange = async (event:any) => {
        let disableButton = !event.complete || this.state.cardholderName.length < 3 || (this.props.showAgreement && !this.state.userAgreementChecked);
        let errorMessage = !event.complete ? event.error?.message : '';

        this.setState({ disableBtn: disableButton, cardDetailsEntered: event.complete, cardDetailsErrorMessage: errorMessage });
    };

    handleCardholderChange = async (event:any) => {
        let text = event.currentTarget.value;
        let showCardholderNameError = text.length < 3;
        let disableButton = !this.state.cardDetailsEntered || showCardholderNameError || (this.props.showAgreement && !this.state.userAgreementChecked);
        let cardholderNameErrorMessage = '';
        let regexInvalidCharacters = /^[^`!@#$%^&*\\=[\]{};:"\\|,.<>/?~]+$|^$/;

        if (showCardholderNameError) {
            cardholderNameErrorMessage = 'Cardholder name must be longer than or equal to 3 characters';
        }

        if (!text.match(regexInvalidCharacters)) {
            cardholderNameErrorMessage = 'Cardholder name contains one or more disallowed characters';
        }

        this.setState({ disableBtn: disableButton, cardholderName: text, cardholderNameErrorMessage: cardholderNameErrorMessage });
    };

    handleAgreementChange = async (event:any) => {
        let userAgreementChecked = event.currentTarget.checked;
        let disableButton = !this.state.cardDetailsEntered || this.state.cardholderName.length < 3 || !userAgreementChecked;

        this.setState({ disableBtn: disableButton, userAgreementChecked: userAgreementChecked });
    };

    handleSubmit = async (event:any) => {
        event.preventDefault();

        if (this.state.disableBtn) {
            return;
        }

        this.props.componentProps.stripePayment.paymentMethod = {
            card: this.props.elements.getElement(CardElement),
            billing_details: {
                name: this.state.cardholderName
            }
        }
        this.props.componentProps.stripePayment.amountToPay = this.props.amountToPay;
        this.props.componentProps.stripePayment.component = this.props.stripe;

        this.props.handlePaymentProcessing();
    };

}